import { Component, forwardRef, Input, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, Validator } from '@angular/forms';
import { PGUtilities } from '../../utilities/pg-utilities';
import { ValidationService } from '../validation.service';
import { LocalizationService } from '../../services/localization.service';

class EmbedData {
    src?:string;
    fullscreen?:boolean;
    ratio?:string;
    width?:number;
    height?:number;
}

@Component({
    selector: 'app-pg-embed-editor',
    templateUrl: './pg-embed-editor.component.html',
    styleUrls: ['./pg-embed-editor.component.scss'],
    providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PgEmbedEditorComponent),
            multi: true
        }, {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => PgEmbedEditorComponent),
            multi: true,
        }],
    standalone: false
})
export class PgEmbedEditorComponent implements OnInit, ControlValueAccessor, Validator {
    @Input() fieldId:string;

    @Input() readonly:boolean;
    @Input() required:boolean;

    @Input() multi:boolean;

    value:Array<EmbedData> = null;
    
    embedRatioOptions:Array<{ value:string, text: string }> = [{
        value: '16:9', text: '16:9'
    }, {
        value: '4:3', text: '4:3'
    }, {
        value: 'specify', text: 'pg-embed-editor.specify-size' 
    }]

    constructor(private validationService:ValidationService, private localizationService:LocalizationService) { }

    translPrefix = '';

    ngOnInit(): void {
        for(let _option of this.embedRatioOptions) {
            if(!/^\d+:\d+$/.test(_option.text)) {
                _option.text = this.localizationService.translate(_option.text)
            }
        }
    }

    private _lastEmitted:string = null

    private _getToEmit() {
        let _result:any = JSON.parse(JSON.stringify(this.value));
        this._normalizeValues(_result);
        if(!this.multi) _result = _result[0] || null;

        return JSON.stringify(_result)
    }

    onValueChange() {       
        if(this._onTouched) this._onTouched();
        if(this._onChange) {
            let _toEmit = this._getToEmit()

            if(this._lastEmitted != _toEmit) {
                this._lastEmitted = _toEmit
                this._onChange(_toEmit);
            }
        }
    }

    _normalizeValues(values:Array<EmbedData>) {
        for(let j = values.length - 1; j >= 0; j--) {
            let _item = values[j]
            if(_item.src) {
                _item.fullscreen = _item.fullscreen || false;
                if(_item.ratio == null) _item.ratio = this.embedRatioOptions[0].value;

                if(_item.ratio != 'specify') {
                    delete _item.width;
                    delete _item.height;
                }
            }
            else {
                values.splice(j, 1)
            }
        }
    }

    addItem() {
        this.value.push({
            ratio: this.embedRatioOptions[0].value
        })
    }

    removeItem(item:EmbedData) {
        let _index = this.value.indexOf(item)

        if(_index != -1) {
            this.value.splice(_index, 1)
        }

        if(this.value.length == 0) {
            this.addItem();
        }

        this.onValueChange();
    }

    // INTERFACCIA ControlValueAccessor

    writeValue(obj:string) {
        this.value = PGUtilities.tryParseJSON(obj);
        if(this.value == null) this.value = []
        else if(!(this.value instanceof Array)) this.value = [this.value]

        this._normalizeValues(this.value)

        if(this.value.length == 0) this.addItem()

        this._lastEmitted = this._getToEmit()
    }

    _onChange;

    registerOnChange(fn: any) {
        this._onChange = fn;
    }

    _onTouched;

    registerOnTouched(fn: any) {
        this._onTouched = fn;
    }

    // INTERFACCIA Validator

    validate()  {
        if(this.required && this.value.length == 0) {
            return {
                required: {
                    valid: false
                }
            }
        }
        
        if(!this.validationService.validateFieldValueFormat('embed', this.value)) {
            return {
                format: {
                    valid: false
                }
            }
        }
    };
}
